import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toastDuration } from '@vnetwork/margarita-shell/toast-options';
import { Download } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@vnetwork/ui/primitives';
import { DialogContent } from '@vnetwork/margarita-shell/DialogContent';
import { useToast } from '@vnetwork/ui/feedback';
import { fetchReport } from '@/lib/vscanner/http-scan-adapter';
import { letterTone, scoreTone } from '@/lib/vscanner/result-view';
import type { ScanTicket } from '@/lib/vscanner/scan-ticket';
import { paths } from '@/lib/paths';
import {
  ITEM_SEVERITY_LABEL,
  REPORT_TABS,
  buildReportView,
  itemsForTab,
  type ReportTab,
  type ReportView,
} from './report-view';
import { PdfDetailsDialog } from './PdfDetailsDialog';
import { downloadReportPdf } from './report-pdf';
import styles from './ScanReportDialog.module.css';

interface ScanReportDialogProps {
  ticket: ScanTicket;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SCORE_RING = 219.9;

const toneClass = (tone: 'good' | 'warn' | 'bad') => styles[`tone-${tone}`];

function useReport(ticket: ScanTicket, enabled: boolean) {
  const [view, setView] = useState<ReportView | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || view) return;
    const run = new AbortController();
    setFailed(false);
    fetchReport(ticket.scanId, ticket.token, run.signal)
      .then((raw) => setView(buildReportView(raw, ticket.host)))
      .catch(() => {
        if (!run.signal.aborted) setFailed(true);
      });
    return () => run.abort();
  }, [enabled, view, ticket]);

  // A different scan replaces the one on screen.
  useEffect(() => setView(null), [ticket.scanId]);

  return { view, failed };
}

export function ScanReportDialog({ ticket, open, onOpenChange }: ScanReportDialogProps) {
  const { view, failed } = useReport(ticket, open);
  const [tab, setTab] = useState<ReportTab>('all');
  const [pdfOpen, setPdfOpen] = useState(false);
  const { show } = useToast();
  const items = useMemo(() => (view ? itemsForTab(view.items, tab) : []), [view, tab]);
  const counts = (tabId: ReportTab) => (view ? itemsForTab(view.items, tabId).length : 0);
  const list = REPORT_TABS.find((t) => t.id === tab)?.list ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className={styles.sheet}>
        <DialogHeader>
          <DialogTitle>{ticket.host}</DialogTitle>
          <DialogDescription>
            VScanner · Security report · Scanned {new Date(ticket.savedAt).toLocaleString()} · Saved to your account
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className={styles.tabs} role="tablist">
            {REPORT_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`${styles.tab} ${tab === t.id ? styles.tabOn : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.id !== 'all' && view ? <span className={styles.tabCount}>{counts(t.id)}</span> : null}
              </button>
            ))}
          </div>

          {failed ? (
            <p className={styles.state} role="alert">
              This report is no longer available. Scan the website again from the sign-in page.
            </p>
          ) : !view ? (
            <p className={styles.state} role="status">
              Loading report…
            </p>
          ) : (
            <>
              {tab === 'all' ? <Overview view={view} /> : null}
              <section className={styles.list} aria-label={list}>
                <div className={styles.listHead}>
                  <h3 className={styles.listTitle}>
                    {list} ({items.length})
                  </h3>
                </div>
                {items.length === 0 ? <p className={styles.state}>Nothing found in this category.</p> : null}
                {items.map((item, i) => (
                  <article key={`${item.title}-${i}`} className={styles.item}>
                    <span className={`${styles.sev} ${styles[`sev-${item.severity}`]}`}>
                      {ITEM_SEVERITY_LABEL[item.severity]}
                    </span>
                    <div className={styles.itemBody}>
                      <div className={styles.itemTitle}>{item.title}</div>
                      {item.detail ? <div className={styles.itemDetail}>{item.detail}</div> : null}
                      <dl className={styles.meta}>
                        {item.location ? (
                          <div className={styles.metaRow}>
                            <dt>Location:</dt> <dd>{item.location}</dd>
                          </div>
                        ) : null}
                        {item.parameter ? (
                          <div className={styles.metaRow}>
                            <dt>Parameter:</dt> <dd>{item.parameter}</dd>
                          </div>
                        ) : null}
                        {item.evidence ? (
                          <div className={styles.metaRow}>
                            <dt>Evidence:</dt> <dd>{item.evidence}</dd>
                          </div>
                        ) : null}
                      </dl>
                      {item.fix ? (
                        <div className={styles.fix}>
                          <div className={styles.fixTitle}>Remediation Guidance</div>
                          <div>{item.fix}</div>
                        </div>
                      ) : null}
                    </div>
                    <span className={styles.cat}>{item.cat}</span>
                  </article>
                ))}
              </section>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <span className={styles.footNote}>
            {view
              ? `The ${view.items.length} issues on ${ticket.host} can be blocked automatically with WAF Free. Setup takes about 3 minutes.`
              : ''}
          </span>
          <Button variant="secondary" disabled={!view} onClick={() => setPdfOpen(true)}>
            <Download size={14} aria-hidden="true" />
            Download PDF
          </Button>
          <Button asChild>
            <Link to={paths.waap.onboarding} onClick={() => onOpenChange(false)}>
              Protect your website now
            </Link>
          </Button>
        </DialogFooter>
        <PdfDetailsDialog
          open={pdfOpen}
          onOpenChange={setPdfOpen}
          host={ticket.host}
          onConfirm={(contact) => {
            setPdfOpen(false);
            if (!view) return;
            downloadReportPdf(view, ticket.host, ticket.savedAt, contact).catch(() =>
              show({ tone: 'danger', duration: toastDuration('danger'), title: 'Could not create the PDF report' }),
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function Overview({ view }: { view: ReportView }) {
  const { result, jsCount } = view;
  const { headers, tls, owasp } = result.metrics;
  const tone = scoreTone(result.score);
  const cards: ReadonlyArray<{
    name: string;
    value: string;
    note: string;
    tone: 'good' | 'warn' | 'bad';
  }> = [
    {
      name: 'Security headers',
      value: `${headers.present} / ${headers.total}`,
      note: `Missing ${headers.missing.join(', ') || 'none'}`,
      tone: letterTone(headers.grade),
    },
    {
      name: 'SSL / TLS',
      value: tls.grade,
      note: `${tls.version}, certificate valid ${tls.daysLeft} days`,
      tone: letterTone(tls.grade),
    },
    {
      name: 'JavaScript',
      value: `${jsCount} obfuscated`,
      note: jsCount ? 'Obfuscated JavaScript code detected' : 'No obfuscated code detected',
      tone: jsCount ? 'warn' : 'good',
    },
    {
      name: 'OWASP Top 10',
      value: `${owasp.flagged} categories`,
      note: owasp.flagged ? `${owasp.issues} findings with surface indicators` : 'No surface indicators',
      tone: owasp.flagged ? 'bad' : 'good',
    },
  ];

  return (
    <div className={styles.overview}>
      <div className={styles.scoreCard}>
        <div className={styles.ring}>
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <circle cx="40" cy="40" r="35" className={styles.ringTrack} />
            <circle
              cx="40"
              cy="40"
              r="35"
              className={`${styles.ringFill} ${toneClass(tone)}`}
              strokeDasharray={SCORE_RING}
              strokeDashoffset={(SCORE_RING * (1 - result.score / 100)).toFixed(1)}
            />
          </svg>
          <div className={styles.ringLabel}>
            <span className={`${styles.score} ${toneClass(tone)}`}>{result.score}</span>
            <span className={styles.scoreMax}>/ 100</span>
          </div>
        </div>
        <p className={styles.scoreNote}>
          Score reflects what is observable from outside. Not a substitute for a penetration test.
        </p>
      </div>
      <div className={styles.cards}>
        {cards.map((c) => (
          <div key={c.name} className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardName}>{c.name}</span>
              <span className={`${styles.dot} ${toneClass(c.tone)}`} />
            </div>
            <div className={styles.cardValue}>{c.value}</div>
            <div className={styles.cardNote}>{c.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
